import { Table as ReactTable } from '@tanstack/react-table';
import React, { useEffect, useState } from 'react';
import { LuChevronLeft, LuChevronRight, LuChevronsLeft, LuChevronsRight } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface TablePaginationProps<T> {
    table: ReactTable<T>;
}

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200, 500, 1000];

export function TablePagination<T>({ table }: TablePaginationProps<T>) {
    const itemCount = table.options.rowCount ?? table.getFilteredRowModel().rows.length;
    const pageSize = table.getState().pagination.pageSize;
    const pageSizeOptions = PAGE_SIZE_OPTIONS.includes(pageSize)
        ? PAGE_SIZE_OPTIONS
        : [...PAGE_SIZE_OPTIONS, pageSize].sort((a, b) => a - b);
    // Page boundaries come from the table: group and expanded rows can shift them without changing the item count.
    const pageCount = Math.max(1, table.getPageCount());
    const currentPageIndex = table.getState().pagination.pageIndex + 1;
    const [gotoValue, setGotoValue] = useState<string>(currentPageIndex.toString());

    // Keep local input in sync when current page changes externally (buttons, data refresh, page size)
    useEffect(() => {
        setGotoValue(currentPageIndex.toString());
    }, [currentPageIndex]);

    const handlePageNumberSubmit = () => {
        const n = Number(gotoValue);
        if (Number.isFinite(n)) {
            const clamped = Math.min(Math.max(Math.trunc(n), 1), pageCount);
            table.setPageIndex(clamped - 1);
        } else {
            setGotoValue(currentPageIndex.toString());
        }
    };

    return (
        <div className="flex items-center justify-between px-2 py-4">
            <div className="flex flex-1 items-center gap-2 text-sm text-gray-600">
                <span>Page</span>
                <span className="font-medium">{currentPageIndex}</span>
                <span>of</span>
                <span className="font-medium">{pageCount}</span>
                <span className="mx-1">·</span>
                <span className="font-medium">{itemCount}</span>
                <span>total items</span>
            </div>

            <div className="flex items-center gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    aria-label="First page"
                    onClick={() => table.setPageIndex(0)}
                    disabled={currentPageIndex === 1}
                    className="h-8 w-8 p-0"
                >
                    <LuChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    aria-label="Previous page"
                    onClick={() => table.previousPage()}
                    disabled={currentPageIndex === 1}
                    className="h-8 w-8 p-0"
                >
                    <LuChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    aria-label="Next page"
                    onClick={() => table.nextPage()}
                    disabled={currentPageIndex >= pageCount}
                    className="h-8 w-8 p-0"
                >
                    <LuChevronRight className="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    aria-label="Last page"
                    onClick={() => table.setPageIndex(pageCount - 1)}
                    disabled={currentPageIndex >= pageCount}
                    className="h-8 w-8 p-0"
                >
                    <LuChevronsRight className="h-4 w-4" />
                </Button>
            </div>

            <div className="flex flex-1 items-center justify-end gap-4">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Go to page</span>
                    <input
                        type="number"
                        value={gotoValue}
                        min={1}
                        max={pageCount}
                        onChange={(e) => {
                            setGotoValue(e.target.value);
                        }}
                        onBlur={handlePageNumberSubmit}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handlePageNumberSubmit();
                                (e.target as HTMLInputElement).blur();
                            }
                        }}
                        aria-label="Go to page number"
                        className="h-8 w-16 rounded-md border border-gray-300 px-2 text-sm"
                    />
                </div>

                <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => {
                        table.setPageSize(Number(value));
                    }}
                >
                    <SelectTrigger className="h-8 w-[120px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {pageSizeOptions.map((size) => (
                            <SelectItem key={size} value={size.toString()}>
                                Show {size}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}
